import { useState } from 'react'
import { useAuditLog, useAuditActions, AuditLogEntry } from '@/hooks/useAuditLog'

export default function AuditLogTab() {
  const [actionFilter, setActionFilter] = useState('')
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: '',
    to: '',
  })

  const { data: actions } = useAuditActions()
  const { data, isLoading, error } = useAuditLog({
    action: actionFilter || undefined,
    from_date: dateRange.from || undefined,
    to_date: dateRange.to || undefined,
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#e8eaed]">Audit Log</h2>
          <p className="text-sm text-[#8b949e]">Track permission changes and impersonation events</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          {/* Action Filter */}
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-full sm:w-48 px-3 py-2 bg-[#141c24] border border-[#2a3542] rounded-lg text-[#e8eaed] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="">All Actions</option>
            {actions?.map((action) => (
              <option key={action.key} value={action.key}>
                {action.name}
              </option>
            ))}
          </select>

          {/* Date From */}
          <input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
            className="w-full sm:w-40 px-3 py-2 bg-[#141c24] border border-[#2a3542] rounded-lg text-[#e8eaed] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="From date"
          />

          {/* Date To */}
          <input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
            className="w-full sm:w-40 px-3 py-2 bg-[#141c24] border border-[#2a3542] rounded-lg text-[#e8eaed] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="To date"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-[#141c24] border border-[#2a3542] rounded-lg p-12 text-center text-[#8b949e]">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          Loading audit log...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
          <p className="text-red-400">Failed to load audit log. Please try again.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && data?.entries.length === 0 && (
        <div className="bg-[#141c24] border border-[#2a3542] rounded-lg p-12 text-center">
          <p className="text-[#8b949e]">No audit log entries found</p>
        </div>
      )}

      {/* Audit Log Table */}
      {!isLoading && !error && data && data.entries.length > 0 && (
        <div className="bg-[#141c24] border border-[#2a3542] rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-[#2a3542]">
            <thead className="bg-[#0a0f14]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Actor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a3542]">
              {data.entries.map((entry) => (
                <AuditLogRow key={entry.id} entry={entry} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Results Info */}
      {data && (
        <p className="text-sm text-[#8b949e]">
          Showing {data.entries.length} of {data.total} entries
        </p>
      )}
    </div>
  )
}

function AuditLogRow({ entry }: { entry: AuditLogEntry }) {
  // Action color mapping
  const actionColors: Record<string, { bg: string; text: string }> = {
    feature_granted: { bg: 'bg-green-500/20', text: 'text-green-400' },
    feature_revoked: { bg: 'bg-red-500/20', text: 'text-red-400' },
    impersonation_started: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
    impersonation_ended: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
    user_updated: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
    tenant_approved: { bg: 'bg-green-500/20', text: 'text-green-400' },
    tenant_suspended: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
    tenant_reactivated: { bg: 'bg-green-500/20', text: 'text-green-400' },
  }

  const actionStyle = actionColors[entry.action] || {
    bg: 'bg-gray-500/20',
    text: 'text-gray-400',
  }

  // Determine target display
  let targetDisplay = '-'
  if (entry.target_user_name) {
    targetDisplay = entry.target_user_name
  } else if (entry.target_tenant_name) {
    targetDisplay = entry.target_tenant_name
  }

  // Format details
  const detailText = entry.feature_name
    ? entry.feature_name
    : entry.details
    ? Object.entries(entry.details)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ')
    : '-'

  return (
    <tr className="hover:bg-[#1c2630] transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-[#e8eaed]">
          {new Date(entry.timestamp).toLocaleDateString()}
        </div>
        <div className="text-xs text-[#8b949e]">
          {new Date(entry.timestamp).toLocaleTimeString()}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionStyle.bg} ${actionStyle.text}`}
        >
          {entry.action_display}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-[#e8eaed]">{entry.actor_name || '-'}</div>
        <div className="text-xs text-[#8b949e]">{entry.actor_email}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-[#e8eaed]">{targetDisplay}</div>
        {entry.target_user_email && (
          <div className="text-xs text-[#8b949e]">{entry.target_user_email}</div>
        )}
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-[#8b949e] max-w-xs truncate" title={detailText}>
          {detailText}
        </div>
      </td>
    </tr>
  )
}
